"use client";

import { createContext, useContext, ReactNode } from "react";
import useSWR from "swr";

// Detect the active locale from window.location.pathname rather than
// `useLocale()` from next-intl. The hook throws when no
// NextIntlClientProvider is in the tree (admin pages, /_not-found,
// build-time prerender, etc.) and SiteConfigProvider sits in BOTH
// customer + admin layouts. URL parsing is provider-agnostic and
// runs in any environment.
const SUPPORTED_LOCALES = new Set([
  "he", "ar", "fr", "es", "de", "zh", "ja", "ko", "ru", "pt", "it", "tr", "nl", "pl",
]);
function detectLocale(): string {
  if (typeof window === "undefined") return "en";
  const m = window.location.pathname.match(/^\/([a-z]{2})(?:\/|$)/);
  if (m && SUPPORTED_LOCALES.has(m[1])) return m[1];
  return "en";
}

/**
 * Customer-facing SiteConfig values pulled from /api/config/public.
 *
 * Purpose: previously every "Free shipping on orders over $500" banner,
 * flat-rate disclaimer, and builder starting-price mention was hardcoded
 * in either constants.ts or directly in translation strings. Changing
 * the threshold in admin had no effect on the customer-facing copy.
 *
 * Now: admin edits SiteConfig → this context picks it up on next fetch
 * → all components using useSiteConfig() re-render with the new value.
 */

export interface PublicSiteConfig {
  freeShippingThreshold: number;
  standardShippingCost: number;
  localDeliveryFee: number;
  giftWrapEnabled: boolean;
  giftWrapPrice: number;
  referralEnabled: boolean;
  referrerRewardAmount: number;
  refereeDiscountAmount: number;
  builderBasePrice: number;
  serviceDepositAmount: number;
  giftCardPresets: number[];
  giftCardExpiryYears: number;
  maxQuantityPerCartItem: number;
  returnInsuranceThreshold: number;
  supportPhone: string;
  /** Toll-free for voice (calls). SMS still uses supportPhone. */
  voicePhone: string;
  supportEmail: string;
  storeAddress: string;
  businessName: string;
  comingSoon: {
    wigs: boolean;
    builder: boolean;
    blog: boolean;
    about: boolean;
  };
  // Booking consolidation feature flag (Phase 2 — wiki/plans/booking-
  // consolidation-2026-04-28.md). When true, /book renders the unified
  // 5-step flow and /consultation redirects to it. Default false until
  // launch-tested.
  unifiedBookingFlow: boolean;
  // Retail service pricing (Manny #147 + #149). Each row:
  // { name, price, priceMax?, description? }. priceMax > price means
  // a range like "$80 – $110" — final quote depends on hair length.
  retailApptServices: Array<{ name: string; price: number; priceMax?: number; description?: string; paymentMode?: "free" | "request_with_skip" | "require" }>;
  retailColorServices: Array<{ name: string; price: number; priceMax?: number; description?: string; paymentMode?: "free" | "request_with_skip" | "require" }>;
  retailRepairServices: Array<{ name: string; price: number; priceMax?: number; description?: string; paymentMode?: "free" | "request_with_skip" | "require" }>;
  retailCutServices: Array<{ name: string; price: number; priceMax?: number; description?: string; paymentMode?: "free" | "request_with_skip" | "require" }>;
  // Per-master sub-catalogs. Each master service now supports its own
  // sub-tier picker; if the array is empty, the parent card stands alone.
  retailWashSetServices: Array<{ name: string; price: number; priceMax?: number; description?: string; paymentMode?: "free" | "request_with_skip" | "require" }>;
  retailDeepCleanServices: Array<{ name: string; price: number; priceMax?: number; description?: string; paymentMode?: "free" | "request_with_skip" | "require" }>;
  retailResizeServices: Array<{ name: string; price: number; priceMax?: number; description?: string; paymentMode?: "free" | "request_with_skip" | "require" }>;
}

// Conservative defaults — match the current hardcoded constants so
// any component reading this before the fetch resolves doesn't flicker.
// These are REPLACED by the server response once it loads, and by a
// cached localStorage copy immediately on subsequent visits (via SWR's
// persistent cache provider).
const DEFAULTS: PublicSiteConfig = {
  freeShippingThreshold: 500,
  standardShippingCost: 15,
  localDeliveryFee: 12.99,
  giftWrapEnabled: true,
  giftWrapPrice: 5,
  referralEnabled: true,
  referrerRewardAmount: 150,
  refereeDiscountAmount: 100,
  builderBasePrice: 2500,
  serviceDepositAmount: 50,
  giftCardPresets: [25, 50, 100, 250, 500],
  giftCardExpiryYears: 1,
  maxQuantityPerCartItem: 10,
  returnInsuranceThreshold: 100,
  supportPhone: "(347) 452-3279",
  voicePhone: "(888) 907-9447",
  supportEmail: "info@rivkywigs.com",
  storeAddress: "1935 Whitesville Rd, Toms River, NJ 08755",
  businessName: "Rivky Wigs",
  // Default ON for all four — better to render coming-soon while
  // SWR fetches the real config than briefly leak the live page.
  comingSoon: { wigs: true, builder: true, blog: true, about: true },
  unifiedBookingFlow: false,
  retailApptServices: [],
  retailColorServices: [],
  retailRepairServices: [],
  retailCutServices: [],
  retailWashSetServices: [],
  retailDeepCleanServices: [],
  retailResizeServices: [],
};

const SiteConfigContext = createContext<PublicSiteConfig>(DEFAULTS);

const fetcher = async (url: string): Promise<PublicSiteConfig> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed (${res.status})`);
  return res.json();
};

export function SiteConfigProvider({ children }: { children: ReactNode }) {
  // Pass active locale so admin-editable retail* catalog rows come
  // back translated for non-English customers (TranslationMemory
  // lookup happens server-side). Per-locale SWR key prevents one
  // locale's cache from leaking into another.
  const locale = detectLocale();
  const url = `/api/config/public?locale=${encodeURIComponent(locale)}`;
  const { data } = useSWR<PublicSiteConfig>(url, fetcher, {
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
  });
  const value = data ?? DEFAULTS;
  return <SiteConfigContext.Provider value={value}>{children}</SiteConfigContext.Provider>;
}

export function useSiteConfig(): PublicSiteConfig {
  return useContext(SiteConfigContext);
}
